<template>
	<v-card color="rgba(41, 41, 47, 0.8)">
		<v-card-title> {{ $t("Addtemporarytrench") }} </v-card-title>
		<v-card-text>
			<v-form v-model="isValid">
				<v-row class="ma-0">
					<v-col cols="6">
						<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
							<tbody>
								<tr>
									<td style="width: 50%">
										{{ $t("Trenchname") }}
									</td>
									<td>
										<v-text-field variant="underlined" hide-details density="compact" v-model="property.name"
											:rules="[rules.validName, rules.maxLength(100), rules.minLength(1)]" autofocus />
									</td>
								</tr>
							</tbody>
						</v-table>
					</v-col>
				</v-row>
				<v-row class="ma-0">
					<v-col>
						<v-row class="ma-0">
							<v-col class="py-0">
								{{ $t("Trenchparameter") }}
							</v-col>
						</v-row>
						<v-row class="ma-0">
							<v-col cols="6">
								<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
									<tbody>
										<tr>
											<td style="width: 50%">{{ $t("Widthoftrench") }}</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.pathSettings.width" :min-value="0.001" :max-value="9999.999"
													:decimal-length="3" />
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("Setsuperelevation") }}
											</td>
											<td>
												<v-select variant="underlined" v-model="property.pathSettings.isSuperElevation"
													:items="slopeTypes" item-title="label" item-value="value" hide-details density="compact"
													class="ma-0" v-on:update:model-value="updateTrenchSettings()"></v-select>
											</td>
										</tr>
										<!-- 片勾配 -->
										<tr v-if="property.pathSettings.isSuperElevation">
											<td>
												<div>
													{{ $t("Superelevation") }}
												</div>
											</td>
											<td>
												<SlopeSettingInput variant="underlined"
													:suffix-to-display="getSuffix(trenchParameterSlopeUnit.superElevation)" :is-editable="true"
													:slope-value="property.pathSettings.leftSlope" :step="0.01" @blur="updateTrenchSettings()"
													v-model.number="property.pathSettings.leftSlope" :max-value="9999.99" :min-value="-9999.99"
													:decimal-length="2" :un-valid-zero="trenchParameterSlopeUnit.superElevation === 'percent'">
												</SlopeSettingInput>
											</td>
										</tr>
										<!-- 横断勾配 -->
										<tr v-else>
											<td>
												<div>
													{{ $t("Crossslope") }}
												</div>
											</td>
											<td>
												<SlopeSettingInput variant="underlined"
													:suffix-to-display="getSuffix(trenchParameterSlopeUnit.crossSlope)" :is-editable="true"
													:slope-value="property.pathSettings.leftSlope" :step="0.01" @blur="updateTrenchSettings()"
													v-model.number="property.pathSettings.leftSlope" :max-value="9999.99" :min-value="0"
													:decimal-length="2" :un-valid-zero="trenchParameterSlopeUnit.crossSlope === 'percent'">
												</SlopeSettingInput>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("InterpolationPitch") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.pathSettings.interpolationPitch" :min-value="0.001" :max-value="9999.999"
													:decimal-length="3" />
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("TRENCH_SURFACE_COLOR") }}
											</td>
											<td>
												<v-menu>
													<template v-slot:activator="{ props }">
														<v-avatar :color="property.pathSettings.surfaceColor" size="20px" v-bind="props">
														</v-avatar>
													</template>
													<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
														v-model="property.pathSettings.surfaceColor"></v-color-picker>
												</v-menu>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("RUBBLING_DESTINATION") }}
											</td>
											<td>
												<v-select variant="underlined" v-model="property.rubbingDestination.target"
													:items="rubbingDestinations" item-title="label" item-value="value" hide-details
													density="compact" class="ma-0"
													@update:model-value="updateRubbingDestinationTarget()"></v-select>
											</td>
										</tr>
										<tr v-if="property.rubbingDestination.target === 'pointcloud'">
											<td>{{ $t("REFERENCE_POINT_CLOUD_ASSET") }}</td>
											<td>
												<v-select variant="underlined" v-model="property.rubbingDestination.id" :items="pointClouds"
													:item-title="(item) => item && item.getName()"
													:item-value="(item) => item && item.getAssetIdNum()" density="compact" hide-details
													class="ma-0">
												</v-select>
											</td>
										</tr>
										<tr v-if="property.rubbingDestination.target === 'design'">
											<td>{{ $t("REFERENCE_DESIGN_ASSET") }}</td>
											<td>
												<v-select variant="underlined" v-model="property.rubbingDestination.id" :items="designs"
													:item-title="(item) => item && item.getName()"
													:item-value="(item) => item && item.getAssetIdNum()" density="compact" hide-details
													class="ma-0" :rules="[rules.required]">
												</v-select>
											</td>
										</tr>
										<tr v-if="property.rubbingDestination.target === 'specified-altitude'">
											<td>{{ $t("SPECIFIED_ALTITUDE") }}</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.rubbingDestination.specifiedAltitude" :min-value="-9999.999"
													:max-value="9999.999" :decimal-length="3" />
											</td>
										</tr>
										<tr v-if="property.rubbingDestination.target === 'flatground'">
											<td>{{ $t("REFERENCE_FLAT_GROUND") }}</td>
											<td>
												<v-select variant="underlined" v-model="property.rubbingDestination.id" :items="flatGrounds"
													:item-title="(item) => item.name" :item-value="(item) => item.id" density="compact"
													hide-details class="ma-0" :rules="[rules.required]">
												</v-select>
											</td>
										</tr>
										<tr v-if="property.rubbingDestination.target === 'reference-topology'">
											<td>{{ $t("REFERENCE_TOPOLOGY") }}</td>
											<td>
												<v-select variant="underlined" v-model="property.rubbingDestination.id" :items="drawing2dImages"
													:item-title="(item) => item.name" :item-value="(item) => item.asset_id" density="compact"
													hide-details class="ma-0" :rules="[rules.required]">
												</v-select>
											</td>
										</tr>
									</tbody>
								</v-table>
							</v-col>
							<v-col cols="6">
								<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
									<tbody>
										<tr>
											<td style="width: 110px">
												{{ $t("Longitudinal_Slope_Type") }}
											</td>
											<td>
												<v-select variant="underlined" v-model="property.pathSettings.longitudinalSlope.slopeType"
													:items="longitudinalSlopeType" item-title="label" item-value="value" hide-details
													density="compact" class="ma-0"></v-select>
											</td>
										</tr>
										<tr v-if="enableSpecifiedElevation">
											<td class="text-pre-wrap">
												<span v-text="$t('Specified_Elevation')"></span>
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.pathSettings.specifiedElevation" :min-value="-9999.999"
													:max-value="9999.999" :decimal-length="3" />
												<span style="word-wrap: break-all; font-size: small; opacity: 0.6;">
													<i>{{ `${$t("DEFAULT_ELEVATION")}：${displayElevation}` }}</i>
												</span>
											</td>
										</tr>
										<tr v-if="enableSlopeValue">
											<td>
												{{ $t("Longitudinal_Slope_Value") }}
											</td>
											<td>
												<SlopeSettingInput :suffix-to-display="getSuffix(trenchLongitudinalSlopeUnit)"
													:is-editable="true" :slope-value="property.pathSettings.longitudinalSlope.slopeValue"
													:step="0.01" v-model.number="property.pathSettings.longitudinalSlope.slopeValue"
													:max-value="trenchLongitudinalSlopeUnit === 'percent' ? 9999.99 : 999.99"
													:min-value="trenchLongitudinalSlopeUnit === 'percent' ? -100 : 0.01" :decimal-length="2"
													:un-valid-zero="trenchLongitudinalSlopeUnit === 'percent'"></SlopeSettingInput>
											</td>
										</tr>
										<tr v-if="enableDepth">
											<td>
												{{ $t("TRENCH_DEPTH") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.pathSettings.longitudinalSlope.depth" :min-value="0.001"
													:max-value="9999.999" :decimal-length="3" />
											</td>
										</tr>
									</tbody>
								</v-table>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
				<v-row class="ma-0">
					<v-col>
						<v-row class="ma-0">
							<v-col class="py-0">
								{{ $t("Cutsetting") }}
							</v-col>
						</v-row>
						<v-row class="ma-0">
							<v-col>
								<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
									<tbody>
										<tr>
											<td style="width: 50%">
												{{ $t("Slopeheight") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													:min-value="0.001" :max-value="99.999" :decimal-length="3" :disabled="cutSlopeCondition"
													@blur="updateTrenchSettings()" v-model="property.cutSettings.trenchHeight" />
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("Slopegradient") }}
											</td>
											<td>
												<SlopeSettingInput :suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.slopeGradient)"
													:is-editable="!property.cutSettings.isLeftRightDifferent"
													:slope-value="property.cutSettings.moundSlope" :step="0.01" @blur="updateTrenchSettings()"
													v-model.number="property.cutSettings.moundSlope"
													:max-value="trenchCutSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99"
													:min-value="0.01" :decimal-length="2" :un-valid-zero="false"></SlopeSettingInput>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("Bermwidth") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													:disabled="cutSlopeCondition" :min-value="0.001" :max-value="99.999" :decimal-length="3"
													@blur="updateTrenchSettings()" v-model="property.cutSettings.shelfWidth" />
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("Bermgradient") }}
											</td>
											<td>
												<SlopeSettingInput :suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.bermGradient)"
													:is-editable="!cutSlopeCondition" :slope-value="property.cutSettings.shelfSlope" :step="0.01"
													@blur="updateTrenchSettings()" v-model.number="property.cutSettings.shelfSlope"
													:max-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
													:min-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01"
													:decimal-length="2" :un-valid-zero="trenchCutSettingSlopeUnit.bermGradient === 'percent'">
												</SlopeSettingInput>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("LeftRightSettings") }}
											</td>
											<td>
												<v-checkbox color="primary" hide-details density="compact" class="ma-0"
													@change="updateTrenchSettings()"
													v-model="property.cutSettings.isLeftRightDifferent"></v-checkbox>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("Thereisberm") }}
											</td>
											<td>
												<v-checkbox color="primary" hide-details density="compact" class="ma-0"
													v-model="property.cutSettings.generateShelf"></v-checkbox>
											</td>
										</tr>
										<tr>
											<td>
												{{ $t("SLOPE_COLOR") }}
											</td>
											<td>
												<v-menu>
													<template v-slot:activator="{ props }">
														<v-avatar :color="property.cutSettings.color" size="20px" v-bind="props">
														</v-avatar>
													</template>
													<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
														v-model="property.cutSettings.color"></v-color-picker>
												</v-menu>
											</td>
										</tr>
									</tbody>
								</v-table>
								<v-expansion-panels>
									<v-expansion-panel style="background-color: rgba(41, 41, 47, 0)">
										<v-expansion-panel-title class="px-4 py-0">
											{{ $t("Leftrightsetting") }}
										</v-expansion-panel-title>
										<v-expansion-panel-text class="pt-2">
											<v-row class="ma-0">
												<v-col class="pa-0">
													{{ $t("Leftcutsetting") }}
													<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)" class="pa-0">
														<tbody>
															<tr>
																<td style="width: 45%">
																	{{ $t("Slopeheight") }}
																</td>
																<td>
																	<DistanceSettingInput variant="underlined" hide-details density="compact"
																		:step="0.001" :disabled="cutLeftRightCondition" :min-value="0.001"
																		:max-value="99.999" :decimal-length="3"
																		v-model="property.cutSettings.leftSlopeHeight" />
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Slopegradient") }}
																</td>
																<td>
																	<SlopeSettingInput
																		:suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.slopeGradient)"
																		:is-editable="property.cutSettings.isLeftRightDifferent"
																		:slope-value="property.cutSettings.leftMoundSlope" :step="0.01"
																		v-model.number="property.cutSettings.leftMoundSlope"
																		:max-value="trenchCutSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99"
																		:min-value="0.01" :decimal-length="2" :un-valid-zero="false"></SlopeSettingInput>
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Bermwidth") }}
																</td>
																<td>
																	<DistanceSettingInput variant="underlined" hide-details density="compact"
																		:step="0.001" :disabled="cutLeftRightCondition" :min-value="0.001"
																		:max-value="99.999" :decimal-length="3"
																		v-model="property.cutSettings.leftShelfWidth" />
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Bermgradient") }}
																</td>
																<td>
																	<SlopeSettingInput
																		:suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.bermGradient)"
																		:is-editable="!cutLeftRightCondition"
																		:slope-value="property.cutSettings.leftShelfSlope" :step="0.01"
																		v-model.number="property.cutSettings.leftShelfSlope"
																		:max-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
																		:min-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01"
																		:decimal-length="2"
																		:un-valid-zero="trenchCutSettingSlopeUnit.bermGradient === 'percent'">
																	</SlopeSettingInput>
																</td>
															</tr>
														</tbody>
													</v-table>
												</v-col>
												<v-col class="pa-0">
													{{ $t("Rightcutsetting") }}
													<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
														<tbody>
															<tr>
																<td style="width: 45%">
																	{{ $t("Slopeheight") }}
																</td>
																<td>
																	<DistanceSettingInput variant="underlined" hide-details density="compact"
																		:step="0.001" :disabled="cutLeftRightCondition" :min-value="0.001"
																		:max-value="99.999" :decimal-length="3"
																		v-model="property.cutSettings.rightSlopeHeight" />
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Slopegradient") }}
																</td>
																<td>
																	<SlopeSettingInput
																		:suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.slopeGradient)"
																		:is-editable="property.cutSettings.isLeftRightDifferent"
																		:slope-value="property.cutSettings.rightMoundSlope" :step="0.01"
																		v-model.number="property.cutSettings.rightMoundSlope"
																		:max-value="trenchCutSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99"
																		:min-value="0.01" :decimal-length="2" :un-valid-zero="false"></SlopeSettingInput>
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Bermwidth") }}
																</td>
																<td>
																	<DistanceSettingInput variant="underlined" hide-details density="compact"
																		:step="0.001" :disabled="cutLeftRightCondition" :min-value="0.001"
																		:max-value="99.999" :decimal-length="3"
																		v-model="property.cutSettings.rightShelfWidth" />
																</td>
															</tr>
															<tr>
																<td>
																	{{ $t("Bermgradient") }}
																</td>
																<td>
																	<SlopeSettingInput
																		:suffix-to-display="getSuffix(trenchCutSettingSlopeUnit.bermGradient)"
																		:is-editable="!cutLeftRightCondition"
																		:slope-value="property.cutSettings.rightShelfSlope" :step="0.01"
																		v-model.number="property.cutSettings.rightShelfSlope"
																		:max-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
																		:min-value="trenchCutSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01"
																		:decimal-length="2"
																		:un-valid-zero="trenchCutSettingSlopeUnit.bermGradient === 'percent'">
																	</SlopeSettingInput>
																</td>
															</tr>
														</tbody>
													</v-table>
												</v-col>
											</v-row>
										</v-expansion-panel-text>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-col>
						</v-row>
					</v-col>
					<v-col>
						<!-- 線形設定値 -->
						<v-row class="ma-0">
							<v-col class="py-0">
								{{ $t("LineWorkparamater") }}
							</v-col>
						</v-row>
						<v-row class="ma-0">
							<v-col>
								<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
									<tbody>
										<!-- 断面幅_左 -->
										<tr>
											<td style="width: 50%">
												{{ $t("WidthofLineWork") }} {{ $t("Left") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.crossSectionSetting.leftWidth" :min-value="1" :max-value="999.999"
													:decimal-length="3" />
											</td>
										</tr>
										<!-- 断面幅_右 -->
										<tr>
											<td>
												<span style="opacity: 0">{{ $t("WidthofLineWork") }}</span>
												{{ $t("Right") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.crossSectionSetting.rightWidth" :min-value="1" :max-value="999.999"
													:decimal-length="3" />
											</td>
										</tr>
										<!-- 測点間隔 -->
										<tr>
											<td>
												{{ $t("STATION_INTERVAL") }}
											</td>
											<td>
												<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
													v-model="property.crossSectionSetting.interpolationPitch" :min-value="1" :max-value="999.999"
													:decimal-length="3" />
											</td>
										</tr>
									</tbody>
								</v-table>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
				<v-row class="ma-0">
					<v-col>
						<v-card-actions class="justify-end">
							<v-btn variant="flat" @click="closeDialog" color="red" :disabled="isTrenchAdded">
								{{ $t("CANCEL") }}
							</v-btn>
							<v-btn variant="flat" @click="addTrench()" color="primary"
								:disabled="!isValid || addTrenchDisabled || isTrenchAdded">
								{{ $t("ADD") }}
							</v-btn>
						</v-card-actions>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
	</v-card>
</template>

<script lang="ts">
import { useUnitDisplay } from "@/composables/useUnitDisplay";
import { TrenchObject } from "@/models/objects/TrenchObject";
import { ObjectType, type ICrossSection } from "@/models/objects/types";
import { notifyError } from "@/utils/notificationUtils";
import rules from "@/utils/validation-rules.js";
import axios from "axios";
import { useStore } from "vuex";
import { LongitudinalSlopeType, UnitTypes } from "../../constant";
import DistanceSettingInput from "./DistanceSettingInput.vue";
import SlopeSettingInput from "./SlopeSettingInput.vue";

export default {
	setup() {
		const { toDisplayDistance } = useUnitDisplay();
		return { toDisplayDistance };
	},
	name: "PropertyDialogTrench",
	components: { SlopeSettingInput, DistanceSettingInput },
	props: {
		id: String,
		type: Number,
	},
	beforeMount() {
		this.property = new TrenchObject();
		// get data from viewer
		this.property.id = this.id;
	},
	async mounted() {
		await this.setElevation();
		this.setSpecifiedElevation();
		this.initSlopeValues();
	},
	data: () => ({
		isValid: false,
		property: null,
		isTrenchAdded: false,
		elevation: 0,
		rules: {
			...rules,
			required: (value) => !!value || "*",
			required0: (value) => value === 0 || !!value || "*",
			validName: (v) => !/[~@#$^*()+=[\]{}|\\,?:<>!'"/;&₹€`%]/.test(v),
		},
		store: useStore(),
	}),
	computed: {
		displayElevation() {
			return this.toDisplayDistance(this.elevation, 3).value;
		},
		designs() {
			return this.store.state.design_list.filter(
				(item) => item.getIsAssetUploaded() === "DONE",
			);
		},
		pointClouds() {
			return this.store.state.point_cloud_list.filter(
				(item) => item.getIsAssetUploaded() === "DONE",
			);
		},
		flatGrounds() {
			return this.store.state.object.objects.filter(
				(item) => item.type === ObjectType.FLAT_GROUND,
			);
		},
		drawing2dImages() {
			return this.store.getters["drawing2dImage/getDrawing2dImageList"].filter(
				(item) => item.getIsAssetUploaded() === "DONE",
			);
		},
		// Disabling condition for cut setting's - shelfSlope, shelfWidth
		cutSlopeCondition() {
			return (
				this.property.cutSettings.isLeftRightDifferent ||
				!this.property.cutSettings.generateShelf
			);
		},
		// Disabling condition for cut setting's - leftShelfWidth, leftShelfSlope, rightShelfWidth, rightShelfSlope
		cutLeftRightCondition() {
			return !(
				this.property.cutSettings.isLeftRightDifferent &&
				this.property.cutSettings.generateShelf
			);
		},
		trenchParameterSlopeUnit() {
			return this.store.state.trenchParameterSlopeUnit;
		},
		trenchCutSettingSlopeUnit() {
			return this.store.state.trenchCutSettingSlopeUnit;
		},
		trenchLongitudinalSlopeUnit() {
			return this.store.state.trenchLongitudinalSlopeUnit;
		},
		fillSettingSlopeUnit() {
			return this.store.state.fillSettingSlopeUnit;
		},
		addTrenchDisabled: function () {
			return (
				this.property.name === null ||
				this.property.name === "" ||
				/^\s*$/.test(this.property.name)
			);
		},
		slopeTypes() {
			return [
				{
					label: this.$t("Crossslope"),
					value: false,
				},
				{
					label: this.$t("Superelevation"),
					value: true,
				},
			];
		},
		enableSlopeValue() {
			return this.isSlopeType("constant");
		},
		enableSpecifiedElevation() {
			return (
				this.isSlopeType("flat") ||
				this.isSlopeType("constant") ||
				this.isSlopeType("default")
			);
		},
		enableDepth() {
			return this.isSlopeType("none");
		},
		rubbingDestinations() {
			const destinations = [];
			if (this.pointClouds.length > 0) {
				destinations.push({
					label: this.$t("POINT_CLOUD"),
					value: "pointcloud",
				});
			}
			if (this.designs.length > 0) {
				destinations.push({
					label: this.$t("DESIGN"),
					value: "design",
				});
			}
			if (this.flatGrounds.length > 0) {
				destinations.push({
					label: this.$t("FLAT_GROUND"),
					value: "flatground",
				});
			}
			if (this.drawing2dImages.length > 0) {
				destinations.push({
					label: this.$t("REFERENCE_TOPOLOGY"),
					value: "reference-topology",
				});
			}
			destinations.push({
				label: this.$t("SPECIFIED_ALTITUDE"),
				value: "specified-altitude",
			});
			return destinations;
		},
		longitudinalSlopeType() {
			return [
				{
					label: this.$t("LS_DEFAULT"),
					value: LongitudinalSlopeType.DEFAULT,
				},
				{
					label: this.$t("LS_FLAT"),
					value: LongitudinalSlopeType.FLAT,
				},
				{
					label: this.$t("LS_CONSTANT"),
					value: LongitudinalSlopeType.CONSTANT,
				},
				{
					label: this.$t("LS_NONE"),
					value: LongitudinalSlopeType.NONE,
				},
			];
		},
	},
	watch: {
		"property.pathSettings.longitudinalSlope.slopeType"() {
			// 床掘下面標高を使用する場合
			if (this.enableSpecifiedElevation) {
				// 変更前が始点の床掘下面標高を使用しない勾配だった場合
				if (this.property.pathSettings.specifiedElevation === null) {
					// デフォルト値を設定
					this.setSpecifiedElevation();
				}
			} else {
				// 未入力にする
				this.property.pathSettings.specifiedElevation = null;
			}

			// 勾配値を設定不可能な場合
			if (!this.enableSlopeValue) {
				// 初期値にする
				this.property.pathSettings.longitudinalSlope.slopeValue = 0;
			}

			// 床掘高さを設定可能な場合
			if (this.enableDepth) {
				// 変更前が床掘高さを設定しない勾配だった場合
				if (this.property.pathSettings.longitudinalSlope.depth === null) {
					// デフォルト値を設定
					this.property.pathSettings.longitudinalSlope.depth = 1;
				}
			} else {
				// 未入力にする
				this.property.pathSettings.longitudinalSlope.depth = null;
			}
		},
	},
	methods: {
		setSpecifiedElevation() {
			const specifiedElevation = this.elevation - 1;
			this.property.pathSettings.specifiedElevation =
				+specifiedElevation.toFixed(3);
		},
		resetProperty() {
			this.property.update(this.property.default);
			this.isTrenchAdded = false;
		},
		getSuffix(unitType) {
			const unit = UnitTypes.find((type) => type.value === unitType);
			return unit.symbol;
		},
		closeDialog() {
			this.store.getters.viewer.deleteTrench(this.id);
			this.$emit("close", false);
			this.property.update(this.property.default);
		},
		setActivePointCloudId() {
			console.log(this.store.getters.activePointCloudId);
			this.property.pathSettings.destination.id =
				this.store.getters.activePointCloudId;
		},
		async addTrench() {
			this.isTrenchAdded = true;
			await this.store.dispatch("executeWithSpinner", async () => {
				try {
					const {
						associatedTerrain,
						surveyPoints,
						horizontalAlignment,
						verticalAlignment,
						ipPointIds,
						rubbingDestination,
					} = this.store.getters.viewer.getTrenchJSON(this.id);

					this.property.associatedTerrain = associatedTerrain;
					this.property.surveyPoints = surveyPoints;
					this.property.horizontalAlignment = horizontalAlignment;
					this.property.verticalAlignment = verticalAlignment;
					this.property.ipPointIds = ipPointIds;
					this.property.rubbingDestination = rubbingDestination;
					const crossSectionSetting = this.property.crossSectionSetting;

					this.store.getters.viewer.deleteTrench(this.id);
					await this.property.loadInViewer();

					// soil volume calculation
					const volume =
						await this.store.getters.viewer.volumeCalculationForTrench(this.id);
					const { cutVolume = 0, fillVolume = 0 } = volume ?? {};
					this.property.cutVolume = cutVolume.toFixed(3);
					this.property.fillVolume = fillVolume.toFixed(3);

					const objConnectionJSON =
						this.store.getters.viewer.getConnectionsInSiteJSON();
					const propsJSON = await this.property.toJSON();

					const res = await axios.post(
						`${import.meta.env.VITE_API_BASE}/obj/${this.$route.query.siteId}`,
						{
							siteId: this.$route.query.siteId,
							objJSON: {},
							objProps: propsJSON,
							objConnectionJSON: objConnectionJSON,
						},
						{
							auth: this.store.state.authObject,
						},
					);
					await this.store.dispatch("object/get_obj", this.id);
					const addedObj = this.store.state.object.objects.find(
						(entity) => entity.id === this.id,
					);
					if (addedObj) {
						addedObj.isLoaded = true;
						addedObj.isVisible.ipLine = true;
						addedObj.isVisible.centerLine = false;
						addedObj.isVisible.surface = true;
						addedObj.isVisible.cutEmbankment = true;
						addedObj.isVisible.crossSection = false;
						// Save cross-section settings for the road
						try {
							const savedSettings: ICrossSection = await this.store.dispatch(
								"lineWorksCrossSection/saveCrossSectionOnCreate",
								{
									object: addedObj,
									siteId: this.$route.query.siteId,
									crossSectionParams: crossSectionSetting,
								},
							);
							if (savedSettings) {
								addedObj.crossSectionSetting = {
									id: savedSettings.cross_section_id,
									leftWidth:
										savedSettings.left_side_width ??
										crossSectionSetting.leftWidth,
									rightWidth:
										savedSettings.right_side_width ??
										crossSectionSetting.rightWidth,
									interpolationPitch:
										savedSettings.station_interval ??
										crossSectionSetting.interpolationPitch,
									samplingSize:
										savedSettings.sampling_size ??
										crossSectionSetting.samplingSize,
								};
							}
						} catch (error) {
							console.error("Failed to save cross-section settings:", error);
						}
					}
				} catch (e) {
					console.error(e);
					this.store.getters.viewer.deleteTrench(this.id);
					notifyError(this, this.$t("TRENCH_SAVE_FAILURE"));
				}
			});
			this.$emit("close", false);
		},
		isNum(val) {
			return typeof val == "number";
		},
		async setElevation() {
			this.elevation = window["viewer"].getReferencePointElevationOfTrench(
				this.id,
			);
		},
		isSlopeType(slopeType) {
			return (
				this.property.pathSettings.longitudinalSlope.slopeType === slopeType
			);
		},
		async updateTrenchSettings() {
			if (this.id === null) return;
			this.property.pathSettings.rightSlope = this.property.pathSettings
				.isSuperElevation
				? -1 * this.property.pathSettings.leftSlope
				: this.property.pathSettings.leftSlope;

			if (!this.property.cutSettings.isLeftRightDifferent) {
				this.property.cutSettings.leftSlopeHeight =
					this.property.cutSettings.trenchHeight;
				this.property.cutSettings.rightSlopeHeight =
					this.property.cutSettings.trenchHeight;

				this.property.cutSettings.leftMoundSlope =
					this.property.cutSettings.moundSlope;
				this.property.cutSettings.rightMoundSlope =
					this.property.cutSettings.moundSlope;

				this.property.cutSettings.leftShelfSlope =
					this.property.cutSettings.shelfSlope;
				this.property.cutSettings.rightShelfSlope =
					this.property.cutSettings.shelfSlope;

				this.property.cutSettings.leftShelfWidth =
					this.property.cutSettings.shelfWidth;
				this.property.cutSettings.rightShelfWidth =
					this.property.cutSettings.shelfWidth;
			}
		},
		updateRubbingDestinationTarget() {
			switch (this.property.rubbingDestination.target) {
				case "specified-altitude":
					const specifiedAltitude =
						this.store.getters.viewer.getAverageHeightOfFirstAndLastPointOfTrench(
							this.id,
						);
					this.property.rubbingDestination.specifiedAltitude = specifiedAltitude
						? +specifiedAltitude.toFixed(3)
						: 0;
					break;
				case "pointcloud":
					this.property.rubbingDestination.id =
						this.pointClouds[0].getAssetIdNum();
					break;
				case "design":
					this.property.rubbingDestination.id = this.designs[0].getAssetIdNum();
					break;
				case "flatground":
					this.property.rubbingDestination.id = this.flatGrounds[0].id;
					break;
				case "reference-topology":
					this.property.rubbingDestination.id =
						this.drawing2dImages[0].asset_id;
					break;
				default:
					this.property.rubbingDestination.id = null;
			}
		},
		initSlopeValues() {
			this.property.pathSettings.leftSlope = 10000;
			this.property.pathSettings.rightSlope = 10000;
		},
	},
};
</script>
